import { useState, useMemo } from 'react';
import { Search, Plus, X, DollarSign, Calendar, User, TrendingUp, Award, ChevronDown, ChevronUp, Trash2, Edit2, Save } from 'lucide-react';
import { showToast } from './Toast';

// Capitalize first letter of each word
const capitalizeWords = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

export function SegurosTab({ lojas, setLojas, onSave }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [newSeguro, setNewSeguro] = useState({ date: '', value: '', clientName: '' });
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [editingSeguro, setEditingSeguro] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', value: '', clientName: '' });

  // Get all sellers from all stores with their store info
  const allSellers = useMemo(() => {
    const sellers = [];
    lojas.forEach(loja => {
      (loja.sellers || []).forEach(seller => {
        sellers.push({
          ...seller,
          name: capitalizeWords(seller.name),
          lojaId: loja.id,
          lojaName: capitalizeWords(loja.name),
          seguros: seller.seguros || []
        });
      });
    });
    return sellers;
  }, [lojas]);

  // Filter sellers by search term
  const filteredSellers = useMemo(() => {
    if (!searchTerm.trim()) return allSellers;
    const term = searchTerm.toLowerCase();
    return allSellers.filter(s => 
      s.name?.toLowerCase().includes(term) ||
      s.lojaName?.toLowerCase().includes(term)
    );
  }, [allSellers, searchTerm]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalSeguros = 0;
    let totalValue = 0;
    allSellers.forEach(seller => {
      totalSeguros += (seller.seguros || []).length;
      (seller.seguros || []).forEach(s => {
        totalValue += parseFloat(s.value) || 0;
      });
    });
    return { totalSeguros, totalValue };
  }, [allSellers]);

  // Get top sellers
  const topSellers = useMemo(() => {
    return [...allSellers]
      .map(s => ({
        ...s,
        seguroCount: (s.seguros || []).length,
        totalValue: (s.seguros || []).reduce((sum, seg) => sum + (parseFloat(seg.value) || 0), 0)
      }))
      .filter(s => s.seguroCount > 0)
      .sort((a, b) => b.seguroCount - a.seguroCount)
      .slice(0, 5);
  }, [allSellers]);

  const handleOpenAddModal = (seller) => {
    setSelectedSeller(seller);
    setNewSeguro({ 
      date: new Date().toISOString().split('T')[0], 
      value: '',
      clientName: ''
    });
    setShowAddModal(true);
  };

  const handleAddSeguro = () => {
    if (!newSeguro.date || !newSeguro.value) {
      showToast('Preencha data e valor', 'error');
      return;
    }

    const seguroToAdd = {
      id: `seg_${Date.now()}`,
      date: newSeguro.date,
      value: parseFloat(newSeguro.value),
      clientName: newSeguro.clientName || '',
      createdAt: new Date().toISOString()
    };

    // Update the seller in the corresponding store
    const newLojas = lojas.map(loja => {
      if (loja.id === selectedSeller.lojaId) {
        return {
          ...loja,
          sellers: (loja.sellers || []).map(seller => {
            if (seller.id === selectedSeller.id) {
              return {
                ...seller,
                seguros: [...(seller.seguros || []), seguroToAdd]
              };
            }
            return seller;
          })
        };
      }
      return loja;
    });

    setLojas(newLojas);
    onSave(newLojas);
    setShowAddModal(false);
    setSelectedSeller(null);
    showToast(`Seguro adicionado para ${selectedSeller.name}!`, 'success');
  };

  const handleDeleteSeguro = (seller, seguroId) => {
    if (!window.confirm('Tem certeza que deseja excluir este seguro?')) return;

    const newLojas = lojas.map(loja => {
      if (loja.id === seller.lojaId) {
        return {
          ...loja,
          sellers: (loja.sellers || []).map(s => {
            if (s.id === seller.id) {
              return {
                ...s,
                seguros: (s.seguros || []).filter(seg => seg.id !== seguroId)
              };
            }
            return s;
          })
        };
      }
      return loja;
    });

    setLojas(newLojas);
    onSave(newLojas);
    showToast('Seguro removido', 'success');
  };

  // Start editing a seguro
  const handleStartEdit = (seller, seguro) => {
    setEditingSeguro({ sellerId: seller.id, lojaId: seller.lojaId, seguroId: seguro.id });
    setEditForm({
      date: seguro.date,
      value: seguro.value.toString(),
      clientName: seguro.clientName || ''
    });
  };

  // Save edited seguro
  const handleSaveEdit = () => {
    if (!editForm.date || !editForm.value) {
      showToast('Preencha data e valor', 'error');
      return;
    }

    const newLojas = lojas.map(loja => {
      if (loja.id === editingSeguro.lojaId) {
        return {
          ...loja,
          sellers: (loja.sellers || []).map(s => {
            if (s.id === editingSeguro.sellerId) {
              return {
                ...s,
                seguros: (s.seguros || []).map(seg => {
                  if (seg.id === editingSeguro.seguroId) {
                    return {
                      ...seg,
                      date: editForm.date,
                      value: parseFloat(editForm.value),
                      clientName: editForm.clientName || '',
                      updatedAt: new Date().toISOString()
                    };
                  }
                  return seg;
                })
              };
            }
            return s;
          })
        };
      }
      return loja;
    });

    setLojas(newLojas);
    onSave(newLojas);
    setEditingSeguro(null);
    setEditForm({ date: '', value: '', clientName: '' });
    showToast('Seguro atualizado!', 'success');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingSeguro(null);
    setEditForm({ date: '', value: '', clientName: '' });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US');
  };

  return (
    <div className="space-y-4 pb-24">
      {/* Header Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={18} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium">Total Seguros</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{totals.totalSeguros}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={18} className="text-blue-400" />
            <span className="text-xs text-blue-400 font-medium">Valor Total</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{formatCurrency(totals.totalValue)}</p>
        </div>
      </div>

      {/* Top Sellers */}
      {topSellers.length > 0 && (
        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award size={18} className="text-yellow-400" />
            <h3 className="text-sm font-bold text-[var(--text-primary)]">Top Vendedores</h3>
          </div>
          <div className="space-y-2">
            {topSellers.map((seller, idx) => (
              <div key={seller.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-black' :
                    idx === 1 ? 'bg-gray-400 text-black' :
                    idx === 2 ? 'bg-amber-700 text-white' :
                    'bg-[var(--bg-chip)] text-[var(--text-secondary)]'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-[var(--text-primary)]">{seller.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400 font-medium">{seller.seguroCount} seguros</span>
                  <span className="text-[var(--text-muted)] text-xs">{formatCurrency(seller.totalValue)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          placeholder="Buscar vendedor por nome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-primary)]"
          data-testid="search-seller"
        />
      </div>

      {/* Sellers List */}
      <div className="space-y-3">
        {filteredSellers.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            {searchTerm ? 'Nenhum vendedor encontrado' : 'Nenhum vendedor cadastrado'}
          </div>
        ) : (
          filteredSellers.map(seller => {
            const seguroCount = (seller.seguros || []).length;
            const totalValue = (seller.seguros || []).reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
            const isExpanded = expandedSeller === `${seller.lojaId}-${seller.id}`;

            return (
              <div
                key={`${seller.lojaId}-${seller.id}`}
                className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] overflow-hidden"
              >
                {/* Seller Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-[var(--accent-primary)]" />
                        <h3 className="font-bold text-[var(--text-primary)]">{seller.name}</h3>
                      </div>
                      <p className="text-xs text-[var(--text-muted)] mt-1">{seller.lojaName}</p>
                      {seller.phone && (
                        <a href={`tel:${seller.phone.replace(/\D/g, '')}`} className="text-xs text-[var(--accent-primary)]">
                          {seller.phone}
                        </a>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-400">{seguroCount}</div>
                      <div className="text-xs text-[var(--text-muted)]">seguros</div>
                      {totalValue > 0 && (
                        <div className="text-xs text-blue-400 mt-1">{formatCurrency(totalValue)}</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleOpenAddModal(seller)}
                      className="flex-1 h-9 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors"
                      data-testid={`btn-add-seguro-${seller.id}`}
                    >
                      <Plus size={16} />
                      Adicionar Seguro
                    </button>
                    {seguroCount > 0 && (
                      <button
                        onClick={() => setExpandedSeller(isExpanded ? null : `${seller.lojaId}-${seller.id}`)}
                        className="h-9 px-3 bg-[var(--bg-chip)] text-[var(--text-secondary)] rounded-lg text-sm flex items-center gap-1 hover:bg-[var(--bg-chip-hover)] transition-colors"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        Ver histórico
                      </button>
                    )}
                  </div>
                </div>

                {/* Seguros History (Expanded) */}
                {isExpanded && seguroCount > 0 && (
                  <div className="border-t border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-4">
                    <h4 className="text-xs font-bold text-[var(--text-secondary)] mb-3">HISTÓRICO DE SEGUROS</h4>
                    <div className="space-y-2">
                      {(seller.seguros || [])
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map(seguro => {
                          const isEditing = editingSeguro?.seguroId === seguro.id && 
                                           editingSeguro?.sellerId === seller.id;
                          
                          if (isEditing) {
                            return (
                              <div
                                key={seguro.id}
                                className="bg-[var(--bg-card)] rounded-lg p-3 border-2 border-[var(--accent-primary)]"
                              >
                                <div className="space-y-3">
                                  <div className="flex gap-2">
                                    <input
                                      type="date"
                                      value={editForm.date}
                                      onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                      className="flex-1 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                                    />
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editForm.value}
                                      onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                      placeholder="Valor"
                                      className="w-28 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                                    />
                                  </div>
                                  <input
                                    type="text"
                                    value={editForm.clientName}
                                    onChange={(e) => setEditForm({ ...editForm, clientName: e.target.value })}
                                    placeholder="Nome do cliente"
                                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={handleCancelEdit}
                                      className="flex-1 h-9 bg-[var(--bg-chip)] text-[var(--text-primary)] rounded-lg text-sm"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      onClick={handleSaveEdit}
                                      className="flex-1 h-9 bg-green-600 text-white rounded-lg text-sm flex items-center justify-center gap-1"
                                    >
                                      <Save size={14} /> Salvar
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div
                              key={seguro.id}
                              className="flex items-center justify-between bg-[var(--bg-card)] rounded-lg p-3 border border-[var(--border-color)]"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                                  <DollarSign size={14} className="text-green-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-green-400">
                                    {formatCurrency(seguro.value)}
                                  </div>
                                  <div className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                                    <Calendar size={10} />
                                    {formatDate(seguro.date)}
                                  </div>
                                  {seguro.clientName && (
                                    <div className="text-xs text-[var(--accent-primary)] flex items-center gap-1 mt-0.5">
                                      <User size={10} />
                                      {seguro.clientName}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleStartEdit(seller, seguro)}
                                  className="p-2 text-[var(--text-secondary)] hover:bg-[var(--bg-chip)] rounded-lg transition-colors"
                                  title="Editar seguro"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteSeguro(seller, seguro.id)}
                                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                  title="Excluir seguro"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add Seguro Modal */}
      {showAddModal && selectedSeller && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--bg-card)] rounded-2xl w-full max-w-sm border border-[var(--border-color)] shadow-xl">
            <div className="p-4 border-b border-[var(--border-color)] flex items-center justify-between">
              <h3 className="font-bold text-[var(--text-primary)]">Adicionar Seguro</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-[var(--bg-chip)] rounded-lg transition-colors"
              >
                <X size={20} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Seller Info */}
              <div className="bg-[var(--bg-chip)] rounded-lg p-3">
                <div className="text-xs text-[var(--text-muted)]">Vendedor</div>
                <div className="font-bold text-[var(--text-primary)]">{selectedSeller.name}</div>
                <div className="text-xs text-[var(--text-secondary)]">{selectedSeller.lojaName}</div>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                  Data do Seguro
                </label>
                <div className="relative">
                  <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="date"
                    value={newSeguro.date}
                    onChange={(e) => setNewSeguro({ ...newSeguro, date: e.target.value })}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                    data-testid="input-seguro-date"
                  />
                </div>
              </div>

              {/* Value */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                  Valor (USD)
                </label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newSeguro.value}
                    onChange={(e) => setNewSeguro({ ...newSeguro, value: e.target.value })}
                    placeholder="0.00"
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                    data-testid="input-seguro-value"
                  />
                </div>
              </div>

              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                  Nome do Cliente
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    type="text"
                    value={newSeguro.clientName}
                    onChange={(e) => setNewSeguro({ ...newSeguro, clientName: e.target.value })}
                    placeholder="Nome do cliente (opcional)"
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-2.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]"
                    data-testid="input-seguro-client"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-[var(--border-color)] flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 h-11 bg-[var(--bg-chip)] text-[var(--text-primary)] rounded-lg font-medium hover:bg-[var(--bg-chip-hover)] transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddSeguro}
                className="flex-1 h-11 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                data-testid="btn-confirm-seguro"
              >
                <Plus size={18} />
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
